import { useRef, useState } from "react";
import type { FormEvent } from "react";
import {
  BUCKET_LABELS,
  PROBLEM_CODES,
  api,
  errorMessage,
  insufficientFunds,
  invalidField,
  problemCode,
  useApiMutation,
  useApiQuery,
} from "../api";
import { useWalletID } from "../wallet";
import type { Grid, Int64, Mission, QueryState, WalletState } from "../api";
import { AsyncBoundary, PageHeader } from "../shell";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Skeleton,
  Stat,
  cx,
  useToast,
} from "../components";
import { useCardEntrance, useCountUp, useShake } from "../motion";
import { compareAmounts, formatDateTime, formatDays, formatTokens, ratio } from "../format";
import {
  LIBELLE_MAX_OCTETS,
  erreurEnveloppe,
  erreurLibelle,
  estimerEnveloppe,
  joursCouverts,
  lireEnveloppe,
  manqueTokens,
  mesurerLibelle,
  resteApresReservation,
} from "./CommandeEstimation";
import styles from "./CommandePage.module.css";

/**
 * Calibres proposés d'un clic pour dimensionner l'enveloppe : une durée au
 * tarif le plus élevé des expertises choisies, comptée en jours ouvrés.
 */
const RACCOURCIS: { jours: number; libelle: string }[] = [
  { jours: 1, libelle: "1 jour" },
  { jours: 5, libelle: "1 semaine" },
  { jours: 10, libelle: "2 semaines" },
  { jours: 20, libelle: "1 mois" },
  { jours: 60, libelle: "1 trimestre" },
];

/**
 * Écran « nouveau projet ».
 *
 * C'est l'écran où le client engage son solde : il décrit le projet, lui
 * attribue des expertises et lui alloue une enveloppe, réservée à l'envoi. Les
 * consultants y imputeront leurs jours au tarif de leur affectation. Un refus
 * est chiffré plutôt que raconté.
 */
export function CommandePage() {
  const walletId = useWalletID();
  const grille = useApiQuery((options) => api.getCurrentGrid(options), []);
  const portefeuille = useApiQuery(
    (options) => api.getWalletState(walletId, options),
    [walletId],
  );

  // La base vide est un état normal tant que le jeu de démonstration n'est pas
  // chargé : `404 grid_not_found` n'est pas une panne à afficher en rouge, mais
  // une absence à expliquer. Sans grille, aucune expertise n'a de tarif — montrer
  // un formulaire ici reviendrait à proposer une action impossible.
  const grilleAbsente =
    grille.data === null &&
    problemCode(grille.error) === PROBLEM_CODES.gridNotFound;

  return (
    <>
      <PageHeader
        eyebrow="Projet"
        title="Nouveau projet"
        description="Nommez le projet, choisissez ses expertises et fixez son budget en tokens : il est mis de côté dès la création."
      />

      {grilleAbsente ? (
        <EmptyState
          icon="🗂"
          title="Aucune grille tarifaire en vigueur."
          description="Sans grille, aucune expertise n'a de tarif : il n'y a rien à commander. Chargez le jeu de démonstration, puis relisez la grille."
          action={
            <Button
              variant="secondary"
              onClick={grille.reload}
              loading={grille.loading}
            >
              Relire la grille
            </Button>
          }
        />
      ) : (
        <AsyncBoundary
          state={grille}
          errorTitle="Grille tarifaire illisible"
          skeleton={<SqueletteCommande />}
          isEmpty={(donnees) => donnees.expertises.length === 0}
          empty={
            <EmptyState
              icon="🗂"
              title="La grille en vigueur ne porte aucune expertise."
              description="Une grille sans expertise ne permet aucune estimation. Vérifiez le jeu de données du catalogue."
            />
          }
        >
          {(donnees) => (
            <Formulaire grille={donnees} portefeuille={portefeuille} />
          )}
        </AsyncBoundary>
      )}
    </>
  );
}

/** Silhouette du formulaire : deux colonnes, comme ce qui va arriver. */
function SqueletteCommande() {
  return (
    <div className={styles.grille} aria-busy="true">
      <Skeleton variant="block" height="26rem" />
      <Skeleton variant="block" height="18rem" />
    </div>
  );
}

type ProprietesFormulaire = {
  grille: Grid;
  /** Lecture du portefeuille : elle situe l'estimation, elle ne la bloque pas. */
  portefeuille: QueryState<WalletState>;
};

function Formulaire({ grille, portefeuille }: ProprietesFormulaire) {
  const walletId = useWalletID();
  const toast = useToast();
  const commande = useApiMutation(api.orderMission);
  // La secousse porte sur le formulaire entier : c'est lui qui est refusé,
  // pas un champ en particulier quand le solde manque.
  const { ref: refFormulaire, shake } = useShake<HTMLFormElement>();
  const refCartes = useCardEntrance<HTMLDivElement>({
    dependencies: [grille.expertises],
  });
  const refEnveloppe = useRef<HTMLInputElement | null>(null);
  const refLibelle = useRef<HTMLInputElement | null>(null);

  const [choisies, setChoisies] = useState<string[]>(() =>
    grille.expertises.length > 0 ? [grille.expertises[0].id] : [],
  );
  const [saisieEnveloppe, setSaisieEnveloppe] = useState("");
  const [libelle, setLibelle] = useState("");
  const [description, setDescription] = useState("");
  const [enveloppeTouchee, setEnveloppeTouchee] = useState(false);
  const [libelleTouche, setLibelleTouche] = useState(false);
  const [soumis, setSoumis] = useState(false);
  const [missionCreee, setMissionCreee] = useState<Mission | null>(null);

  const expertisesChoisies = grille.expertises.filter((e) => choisies.includes(e.id));
  // Le tarif le plus élevé des expertises choisies dimensionne les calibres :
  // une enveloppe calibrée ainsi couvre la durée quel que soit le consultant.
  const tarifMax = expertisesChoisies.reduce<Int64>(
    (max, e) => (compareAmounts(e.tokens_per_day, max) > 0 ? e.tokens_per_day : max),
    "0",
  );

  const enveloppe = lireEnveloppe(saisieEnveloppe) ?? "0";
  const refCompteur = useCountUp<HTMLSpanElement>(enveloppe);

  const disponible = portefeuille.data?.balance.available ?? null;
  const portefeuilleAbsent =
    portefeuille.data === null &&
    problemCode(portefeuille.error) === PROBLEM_CODES.walletNotFound;
  const manque = disponible === null ? null : manqueTokens(enveloppe, disponible);

  // Un refus porte ses chiffres : on lit le code machine et les montants, jamais
  // le texte, qui est traduisible.
  const champRefuse = invalidField(commande.error);

  const erreurMontant =
    (enveloppeTouchee || soumis ? erreurEnveloppe(saisieEnveloppe) : null) ??
    (champRefuse === "envelope_tokens" ? errorMessage(commande.error) : null);
  const erreurIntitule =
    (libelleTouche || soumis ? erreurLibelle(libelle) : null) ??
    (champRefuse === "label" ? errorMessage(commande.error) : null);
  const erreurExpertises =
    (soumis && choisies.length === 0 ? "Attribuez au moins une expertise au projet." : null) ??
    (champRefuse === "expertise_ids" ? errorMessage(commande.error) : null);

  const octetsLibelle = mesurerLibelle(libelle);

  /**
   * Une saisie modifiée décrit une autre commande que la précédente : ni le
   * refus ni le reçu affichés ne la concernent encore, et les laisser côte à
   * côte avec un nouveau chiffre serait le meilleur moyen de faire lire le
   * mauvais montant.
   */
  function nouvelleIntention() {
    if (commande.error !== null) {
      commande.reset();
    }
    setMissionCreee(null);
  }

  function basculerExpertise(id: string) {
    setChoisies((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
    nouvelleIntention();
  }

  function calibrer(jours: number) {
    const montant = estimerEnveloppe(tarifMax, jours);
    setSaisieEnveloppe(formatTokens(montant).replace(/[\s\u202f\u00a0]/g, ""));
    setEnveloppeTouchee(true);
    nouvelleIntention();
  }

  async function envoyer(evenement: FormEvent<HTMLFormElement>) {
    evenement.preventDefault();
    setSoumis(true);

    // `POST /missions` n'est **pas** idempotent, et c'est le contrat qui le dit :
    // deux envois identiques créent deux projets, donc deux enveloppes
    // réservées. La désactivation du bouton pendant l'envoi (`loading`) et ce
    // garde-fou sont la seule barrière qui existe — le serveur n'en a aucune.
    if (commande.pending) {
      return;
    }

    const montantRefuse = erreurEnveloppe(saisieEnveloppe);
    const intituleRefuse = erreurLibelle(libelle);
    const montant = lireEnveloppe(saisieEnveloppe);
    if (montantRefuse !== null || intituleRefuse !== null || montant === null || choisies.length === 0) {
      shake();
      (intituleRefuse !== null ? refLibelle : refEnveloppe).current?.focus();
      return;
    }

    const creee = await commande.run(walletId, {
      label: libelle.trim(),
      description: description.trim(),
      envelope_tokens: montant,
      expertise_ids: choisies,
    });
    if (creee === null) {
      shake();
      return;
    }

    setMissionCreee(creee);
    setSoumis(false);
    toast.notify({
      tone: "success",
      title: "Projet créé",
      description: `${formatTokens(creee.envelope_tokens)} tokens réservés pour « ${creee.label} ». L'ESN y affectera les consultants.`,
    });
    // L'enveloppe vient de quitter le disponible : le solde affiché à côté du
    // formulaire doit le dire tout de suite.
    portefeuille.reload();
  }

  function recommencer() {
    setMissionCreee(null);
    setLibelle("");
    setDescription("");
    setLibelleTouche(false);
    setSoumis(false);
    commande.reset();
    refLibelle.current?.focus();
  }

  const jours = compareAmounts(tarifMax, "0") > 0 ? joursCouverts(enveloppe, tarifMax) : null;

  return (
    <div className={styles.grille}>
      <form
        ref={refFormulaire}
        className={styles.formulaire}
        onSubmit={(evenement) => void envoyer(evenement)}
        noValidate
      >
        <fieldset className={styles.bloc} disabled={commande.pending}>
          <legend className={styles.legende}>Projet</legend>

          <Field
            label="Nom du projet"
            required
            error={erreurIntitule}
            hint={`Le nom affiché partout pour ce projet. ${octetsLibelle} / ${LIBELLE_MAX_OCTETS}.`}
          >
            {(controle) => (
              <input
                {...controle}
                ref={refLibelle}
                type="text"
                autoComplete="off"
                placeholder="ex. Refonte du portail client"
                value={libelle}
                onChange={(evenement) => {
                  setLibelle(evenement.target.value);
                  nouvelleIntention();
                }}
                onBlur={() => setLibelleTouche(true)}
              />
            )}
          </Field>

          <Field label="Description" hint="Ce que le projet doit produire. Facultatif.">
            {(controle) => (
              <textarea
                {...controle}
                rows={3}
                maxLength={4000}
                value={description}
                onChange={(evenement) => {
                  setDescription(evenement.target.value);
                  nouvelleIntention();
                }}
              />
            )}
          </Field>
        </fieldset>

        <fieldset className={styles.bloc} disabled={commande.pending}>
          <legend className={styles.legende}>
            Expertises du projet
            <Badge tone="info" size="sm" className={styles.versionGrille}>
              {grille.label}
            </Badge>
          </legend>
          <p className={styles.mention}>
            Chaque consultant sera affecté sur l'une d'elles, à son tarif journalier.
          </p>
          {erreurExpertises !== null && (
            <p className={styles.refusDetail} role="alert">
              {erreurExpertises}
            </p>
          )}

          <div ref={refCartes} className={styles.expertises}>
            {grille.expertises.map((candidat) => (
              <label
                key={candidat.id}
                className={cx(
                  styles.expertise,
                  choisies.includes(candidat.id) && styles.profilChoisi,
                )}
                data-anim="card"
              >
                <input
                  type="checkbox"
                  name="expertises"
                  className={styles.radio}
                  value={candidat.id}
                  checked={choisies.includes(candidat.id)}
                  onChange={() => basculerExpertise(candidat.id)}
                />
                <span className={styles.profilTextes}>
                  <span className={styles.profilLibelle}>{candidat.label}</span>
                </span>
                <span className={styles.profilTarif}>
                  <strong className="lde-tabular">
                    {formatTokens(candidat.tokens_per_day)}
                  </strong>
                  <span className={styles.profilUnite}>tokens/j</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset className={styles.bloc} disabled={commande.pending}>
          <legend className={styles.legende}>Budget</legend>

          <Field
            label="Budget du projet (tokens)"
            required
            error={erreurMontant}
            hint={
              jours === null || lireEnveloppe(saisieEnveloppe) === null
                ? "Le volume réservé sur votre solde. Les calibres ci-dessous le dimensionnent en durée."
                : `Couvre ${formatDays(jours)} au tarif le plus élevé des expertises choisies.`
            }
          >
            {(controle) => (
              <input
                {...controle}
                ref={refEnveloppe}
                type="text"
                inputMode="decimal"
                autoComplete="off"
                placeholder="ex. 200"
                value={saisieEnveloppe}
                onChange={(evenement) => {
                  setSaisieEnveloppe(evenement.target.value);
                  nouvelleIntention();
                }}
                onBlur={() => setEnveloppeTouchee(true)}
              />
            )}
          </Field>

          <div className={styles.raccourcis}>
            {RACCOURCIS.map((raccourci) => (
              <Button
                key={raccourci.jours}
                size="sm"
                variant="ghost"
                disabled={compareAmounts(tarifMax, "0") <= 0}
                onClick={() => calibrer(raccourci.jours)}
              >
                {raccourci.libelle}
              </Button>
            ))}
          </div>
        </fieldset>

        {commande.error !== null && champRefuse === null && (
          <Refus erreur={commande.error} />
        )}

        <div className={styles.actions}>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={commande.pending}
          >
            {commande.pending
              ? "Réservation en cours…"
              : `Créer le projet et réserver ${formatTokens(enveloppe)} tokens`}
          </Button>
          <p className={styles.avertissement}>
            Le budget est mis de côté dès la création. Un second envoi créerait
            un second projet.
          </p>
        </div>
      </form>

      <aside className={styles.panneau} aria-label="Budget du projet">
        <Card
          title="Budget du projet"
          subtitle={
            expertisesChoisies.length === 0
              ? "Aucune expertise choisie"
              : expertisesChoisies.map((e) => e.label).join(" · ")
          }
        >
          <Stat
            className={styles.aPlat}
            label="À réserver"
            // L'élément reste vide : c'est `useCountUp` qui y écrit, et qui
            // écrit la chaîne exacte à la dernière image.
            value={<span ref={refCompteur} className="lde-tabular" />}
            unit="tokens"
            bucket="reserved"
            emphasis
            hint="Chaque consultant y déclarera ses jours, au tarif de son expertise."
            ratio={disponible === null ? undefined : ratio(enveloppe, disponible)}
          />

          <Verdict
            disponible={disponible}
            estimation={enveloppe}
            manque={manque}
            portefeuilleAbsent={portefeuilleAbsent}
            rechargerPortefeuille={portefeuille.reload}
            portefeuilleEnCours={portefeuille.loading}
          />
        </Card>

        {missionCreee !== null && (
          <Card
            title="Projet créé"
            subtitle={`Créé le ${formatDateTime(missionCreee.created_at)}`}
            actions={
              <Badge tone="success" dot>
                Enveloppe réservée
              </Badge>
            }
            footer={
              <Button variant="secondary" onClick={recommencer}>
                Créer un autre projet
              </Button>
            }
          >
            <dl className={styles.recap}>
              <div className={styles.recapLigne}>
                <dt>Projet</dt>
                <dd>{missionCreee.label}</dd>
              </div>
              <div className={styles.recapLigne}>
                <dt>Enveloppe</dt>
                <dd className="lde-tabular">
                  {formatTokens(missionCreee.envelope_tokens)} tokens
                </dd>
              </div>
              <div className={styles.recapLigne}>
                <dt>Expertises</dt>
                <dd>{missionCreee.expertise_ids.length}</dd>
              </div>
              <div className={styles.recapLigne}>
                <dt>Référence</dt>
                <dd className={styles.reference}>{missionCreee.id}</dd>
              </div>
            </dl>
          </Card>
        )}
      </aside>
    </div>
  );
}

type ProprietesVerdict = {
  disponible: Int64 | null;
  estimation: Int64;
  manque: Int64 | null;
  portefeuilleAbsent: boolean;
  rechargerPortefeuille: () => void;
  portefeuilleEnCours: boolean;
};

/**
 * Ce que l'estimation donne face au solde : le client doit voir **avant**
 * d'envoyer si la commande passe.
 *
 * Le bouton d'envoi reste actif même quand le solde ne suffit pas : le solde
 * affiché peut dater de quelques secondes, et c'est le grand livre qui tranche.
 * L'avertissement dit ce qui va se passer, il n'interdit pas d'essayer.
 */
function Verdict({
  disponible,
  estimation,
  manque,
  portefeuilleAbsent,
  rechargerPortefeuille,
  portefeuilleEnCours,
}: ProprietesVerdict) {
  if (portefeuilleAbsent) {
    return (
      <div className={cx(styles.verdict, styles.verdictNeutre)}>
        <Badge tone="warning" dot>
          Solde inconnu
        </Badge>
        <p>
          Ce portefeuille n'existe pas encore : impossible de vérifier la
          couverture avant l'envoi. La commande sera refusée par le service tant
          qu'il n'a rien reçu.
        </p>
      </div>
    );
  }

  if (disponible === null) {
    return (
      <div className={cx(styles.verdict, styles.verdictNeutre)}>
        <Badge tone="neutral" dot>
          Solde indisponible
        </Badge>
        <p>Le solde n'a pas pu être lu : la couverture n'est pas vérifiable ici.</p>
        <Button
          size="sm"
          variant="ghost"
          onClick={rechargerPortefeuille}
          loading={portefeuilleEnCours}
        >
          Relire le solde
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.soldes}>
      <Stat
        className={styles.aPlat}
        label="Disponible aujourd'hui"
        value={<span className="lde-tabular">{formatTokens(disponible)}</span>}
        unit="tokens"
        bucket="available"
      />

      {manque === null ? (
        <div className={cx(styles.verdict, styles.verdictOk)}>
          <Badge tone="success" dot>
            Couvert
          </Badge>
          <p>
            Il resterait{" "}
            <strong className="lde-tabular">
              {formatTokens(resteApresReservation(disponible, estimation))}
            </strong>{" "}
            tokens disponibles après réservation.
          </p>
        </div>
      ) : (
        <div className={cx(styles.verdict, styles.verdictKo)} role="status">
          <Badge tone="danger" dot>
            Insuffisant
          </Badge>
          <p>
            Il vous manque{" "}
            <strong className="lde-tabular">{formatTokens(manque)}</strong> tokens
            pour cette commande.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Le refus renvoyé par le service, chiffré quand il l'est.
 *
 * `insufficient_funds` porte `requested` et `available` précisément pour qu'on
 * écrive « il vous manque N tokens » plutôt que « une erreur est survenue » :
 * l'un dit quoi faire, l'autre laisse le client devant un mur.
 */
function Refus({ erreur }: { erreur: unknown }) {
  const solde = insufficientFunds(erreur);

  if (solde === null) {
    return (
      <Alert role="alert" title="Projet refusé">
        {errorMessage(erreur)}
      </Alert>
    );
  }
  return (
    <Alert
      role="alert"
      title={
        <>
          Il vous manque{" "}
          <strong>{formatTokens(manqueTokens(solde.requested, solde.available) ?? "0")}</strong>{" "}
          tokens.
        </>
      }
    >
      La commande demande <strong>{formatTokens(solde.requested)}</strong> tokens ; le seau
      «&nbsp;{BUCKET_LABELS[solde.bucket]}&nbsp;» n'en porte que{" "}
      <strong>{formatTokens(solde.available)}</strong>. Rien n'a été réservé : créditez un pack, ou
      réduisez l'enveloppe.
    </Alert>
  );
}
